/**
 * Slice LEVI-VOICE-01 · Semantic voice preference model.
 *
 * Single source of truth for Levi's TTS preferences. Stores semantic
 * choices (language, gender preference, speaking rate) rather than
 * device-specific voice names, so a later hosted-TTS upgrade can
 * drop in behind the same model without touching entry points.
 *
 * Storage: window.localStorage under jc:levi:voice:*.
 * Change notification: subscribeVoicePrefs(fn) → unsubscribe.
 */

const K_TTS_ENABLED = "jc:levi:voice:tts_enabled";
const K_LANGUAGE = "jc:levi:voice:language";
const K_GENDER = "jc:levi:voice:gender_preference";
const K_RATE = "jc:levi:voice:speaking_rate";
const K_VOICE_URI = "jc:levi:voice:preferred_voice_uri";

// Legacy key from the earlier LV-02 slice — read once on migration so
// pre-existing user preference for TTS on/off is preserved.
const LEGACY_TTS_KEY = "jc:levi:tts:enabled";

export const GENDER_CHOICES = Object.freeze([
  "friendly_male",
  "friendly_female",
  "no_preference",
]);

export const LANGUAGE_CHOICES = Object.freeze(["en", "es"]);

const RATE_MIN = 0.8;
const RATE_MAX = 1.2;
const RATE_DEFAULT = 1.0;

// ---------- Storage helpers ----------
const _read = (k) => {
  try { return typeof localStorage !== "undefined" ? localStorage.getItem(k) : null; }
  catch { return null; }
};
const _write = (k, v) => {
  try {
    if (typeof localStorage === "undefined") return;
    if (v === null || v === undefined) localStorage.removeItem(k);
    else localStorage.setItem(k, String(v));
  } catch { /* noop */ }
};

// ---------- Change subscription ----------
const _listeners = new Set();
const _notify = () => {
  for (const fn of _listeners) {
    try { fn(); } catch { /* ignore */ }
  }
};
export const subscribeVoicePrefs = (fn) => {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
};

// ---------- Migration ----------
let _migrated = false;
const _migrateOnce = () => {
  if (_migrated) return;
  _migrated = true;
  // Only run when localStorage exists.
  if (_read(K_TTS_ENABLED) === null) {
    const legacy = _read(LEGACY_TTS_KEY);
    if (legacy === "1" || legacy === "0") _write(K_TTS_ENABLED, legacy);
  }
};

// ---------- TTS on/off ----------
export const getTtsEnabled = () => {
  _migrateOnce();
  const v = _read(K_TTS_ENABLED);
  if (v === "1") return true;
  if (v === "0") return false;
  return true; // default on
};
export const setTtsEnabled = (on) => {
  _write(K_TTS_ENABLED, on ? "1" : "0");
  // Keep legacy key in sync so leviSpeech.isTtsEnabled() reads the
  // same source until we finish removing it in a follow-up slice.
  _write(LEGACY_TTS_KEY, on ? "1" : "0");
  _notify();
};

// ---------- Language ----------
export const getLanguage = (fallback = "en") => {
  const v = _read(K_LANGUAGE);
  if (LANGUAGE_CHOICES.includes(v)) return v;
  return LANGUAGE_CHOICES.includes(fallback) ? fallback : "en";
};
export const setLanguage = (lang) => {
  if (!LANGUAGE_CHOICES.includes(lang)) return;
  _write(K_LANGUAGE, lang);
  _notify();
};

// ---------- Gender preference ----------
export const getGenderPreference = () => {
  const v = _read(K_GENDER);
  if (GENDER_CHOICES.includes(v)) return v;
  return "no_preference";
};
export const setGenderPreference = (g) => {
  if (!GENDER_CHOICES.includes(g)) return;
  _write(K_GENDER, g);
  // Invalidate cached voice URI — ranking will recompute next speak.
  _write(K_VOICE_URI, null);
  _notify();
};

// ---------- Speaking rate ----------
export const getSpeakingRate = () => {
  const raw = _read(K_RATE);
  const n = raw == null ? RATE_DEFAULT : parseFloat(raw);
  if (!isFinite(n)) return RATE_DEFAULT;
  return Math.max(RATE_MIN, Math.min(RATE_MAX, n));
};
export const setSpeakingRate = (rate) => {
  const n = parseFloat(rate);
  if (!isFinite(n)) return;
  const clamped = Math.max(RATE_MIN, Math.min(RATE_MAX, n));
  _write(K_RATE, String(clamped));
  _notify();
};
export const RATE_BOUNDS = Object.freeze({ min: RATE_MIN, max: RATE_MAX, step: 0.05, default: RATE_DEFAULT });

// ---------- Preferred voice URI (cache only) ----------
// This is a CACHE of a device-specific voice URI. It is NEVER the
// canonical preference — the canonical preference is the semantic
// tuple (language, gender_preference). When the cached URI is no
// longer available on the device, callers recompute via the ranker.
export const getPreferredVoiceUri = () => _read(K_VOICE_URI);
export const setPreferredVoiceUri = (uri) => {
  _write(K_VOICE_URI, uri || null);
  // Do NOT notify listeners — this is an internal cache write, not a
  // user preference change.
};

// ---------- Consolidated snapshot ----------
export const readVoicePrefs = (appLangFallback = "en") => {
  _migrateOnce();
  return {
    tts_enabled: getTtsEnabled(),
    language: getLanguage(appLangFallback),
    gender_preference: getGenderPreference(),
    speaking_rate: getSpeakingRate(),
    preferred_voice_uri: getPreferredVoiceUri(),
  };
};
